import React, { useContext, useState } from 'react';
import { useUpdatePasswordMutation } from '../../../generated/graphql';
import { Box, IconButton } from '@mui/material';
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { Colors } from '../../../utils/theme';
import { ErrorTexts } from './CreateUser';
import { getRequiredFieldLabelColor } from '../../../utils/styleUtils';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import InputComponent from '../../Common/Fields/InputComponent';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';

type IErrors = {
  password: string | null;
  confirmPassword: string | null;
};

const defaultErrorObj: IErrors = {
  password: null,
  confirmPassword: null
};

const errorTexts = {
  pass: 'Password too short',
  confirmPass: 'Confirm password too short'
};

const UserPasswordTab: React.FC = () => {
  const { dispatch, state } = useContext(AppContext);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] = useState(defaultErrorObj);
  const [updatePass, { loading }] = useUpdatePasswordMutation();

  const handlePassChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (value.trim().length > 1) {
      setFormErrors({ ...formErrors, password: null });
    } else {
      setFormErrors({ ...formErrors, password: errorTexts.pass });
    }
    setPassword(value.trim());
  };

  const handleConfirmPassChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (value.trim().length > 1) {
      setFormErrors({ ...formErrors, confirmPassword: null });
    } else {
      setFormErrors({ ...formErrors, confirmPassword: errorTexts.confirmPass });
    }
    setConfirmPassword(value);
  };

  const resetForm = () => {
    setPassword('');
    setConfirmPassword('');
    setFormErrors(defaultErrorObj);
  };

  const handleSaveClicked = () => {
    let hasErrors = false;
    const errors: IErrors = {
      password: null,
      confirmPassword: null
    };
    if (password.length < 2) {
      hasErrors = true;
      errors.password = 'Password too short';
    }
    if (confirmPassword.length < 2) {
      hasErrors = true;
      errors.confirmPassword = 'Confirm password too short';
    }
    if (!hasErrors && password !== confirmPassword) {
      hasErrors = true;
      errors.confirmPassword = ErrorTexts.passwordMismatch;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      dispatch({
        type: Actions.ShowConfirmation,
        payload: {
          text: `Are you sure you want to create new password for ${state?.selectedUser?.username}?`,
          agreeAction: submitForm
        }
      });
    }
  };

  const submitForm = () => {
    updatePass({
      variables: {
        id: parseInt(state?.selectedUser?.id as string),
        newPass: password
      },
      update() {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: `You have successfully updated ${state?.selectedUser?.username}'s password`
          }
        });
        resetForm();
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: e.message,
          autoHide: 6000
        }
      });
    });
    dispatch({
      type: Actions.HideConfirmation
    });
  };

  return (
    <Box id="user-details">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
          paddingTop: '250px',
          paddingLeft: '25%',
          paddingRight: '25%',
          width: '100%'
        }}
      >
        <InputComponent
          id="user-new-password"
          label="New Password"
          value={password}
          type={showPassword ? 'text' : 'password'}
          onChange={handlePassChange}
          errorMsg={formErrors.password}
          disabled={loading}
          additionalStyle={{ gridColumn: 'span 1', height: '46px' }}
          startAdornment={
            <Lock
              style={{
                marginRight: '12px',
                color: getRequiredFieldLabelColor(!loading, formErrors.password)
              }}
            />
          }
          endAdornment={
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
            >
              {showPassword ? (
                <Visibility style={{ color: Colors.skyBlue }} />
              ) : (
                <VisibilityOff style={{ color: Colors.skyBlue }} />
              )}
            </IconButton>
          }
        />
        <InputComponent
          id="user-new-confirmpassword"
          label="Confirm New Password"
          value={confirmPassword}
          type={showPassword ? 'text' : 'password'}
          onChange={handleConfirmPassChange}
          errorMsg={formErrors.confirmPassword}
          disabled={loading}
          additionalStyle={{ gridColumn: 'span 1', marginTop: '30px', height: '46px' }}
          startAdornment={
            <Lock
              style={{
                marginRight: '12px',
                color: getRequiredFieldLabelColor(!loading, formErrors.confirmPassword)
              }}
            />
          }
          endAdornment={
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
            >
              {showPassword ? (
                <Visibility style={{ color: Colors.skyBlue }} />
              ) : (
                <VisibilityOff style={{ color: Colors.skyBlue }} />
              )}
            </IconButton>
          }
        />
        <Box className="centralize" sx={{ gridColumn: 'span 1', marginTop: '130px' }}>
          <CancelButton id="cancel-user-new-password-button" onClick={resetForm} text={'Cancel'} />
          <SaveButton
            id="save-user-new-password-button"
            onClick={handleSaveClicked}
            text={'Save'}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default UserPasswordTab;
